import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography, Stack, Divider } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import {
  CommandIcons,
  ComponentsIcons,
  UsersIcons,
} from '../../../icons/icons';
import { fetchOrganizationUsers } from '../../../utils/redux_store/features/organization_user_reducer';
import { fetchUserGroups } from '../../../utils/redux_store/features/user_group_reducer';
import { fetchUserRoles } from '../../../utils/redux_store/features/user_role_reducer';
import { RootState, store } from '../../../utils/redux_store/store';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Tabs } from '../../assessment/view/tabs/tabs';
import { GroupManagement } from './group_management/group_management';
import { RoleManagement } from './role_management/role_management';

export const AccessManagement = () => {
  const { t } = useTranslation(NS.adminPages);
  const [activeTab, setActiveTab] = useState<number>(0);
  const tabTitles = [
    t(ADMIN_PAGES_KEYS.accessManagement.tabs.groups),
    t(ADMIN_PAGES_KEYS.accessManagement.tabs.roles),
  ];
  const userCount = useSelector(
    (state: RootState) => state.organizationUsers?.organizationUserList?.length ?? 0,
  );
  const groupCount = useSelector(
    (state: RootState) => state.userGroups?.userGroupList?.length ?? 0,
  );
  const roleCount = useSelector(
    (state: RootState) => state.userRoles?.userRoleList?.length ?? 0,
  );

  useEffect(() => {
    store.dispatch(fetchOrganizationUsers());
    store.dispatch(fetchUserGroups());
    store.dispatch(fetchUserRoles());
  }, []);

  const renderTabContent = () => {
    if (activeTab === 0) {
      return <GroupManagement />;
    } else if (activeTab === 1) {
      return <RoleManagement />;
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SettingsSubNav currentPath='/access-management' />
      <Box sx={{ width: '100%' }}>
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            p: 1,
            bgcolor: 'background.paper',
            borderColor: 'divider',
            borderRadius: '6px',
            boxShadow: 'none',
            justifyContent: 'center',
            maxWidth: 520,
            mx: 'auto',
            mb: 2.5,
          }}
        >
          {/* Users */}
          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                bgcolor: '#f8fafc',
                color: '#475569',
                '& svg': { width: 18, height: 18 },
              }}
            >
              <UsersIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
              {userCount}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.accessManagement.users)}
            </Typography>
          </Stack>

          <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

          {/* Groups */}
          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                bgcolor: '#f8fafc',
                color: '#475569',
                '& svg': { width: 18, height: 18 },
              }}
            >
              <ComponentsIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
              {groupCount}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.accessManagement.groups)}
            </Typography>
          </Stack>

          <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />

          {/* Roles */}
          <Stack direction='row' alignItems='center' spacing={1} sx={{ px: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                bgcolor: '#f8fafc',
                color: '#475569',
                '& svg': { width: 18, height: 18 },
              }}
            >
              <CommandIcons />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
              {roleCount}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {t(ADMIN_PAGES_KEYS.accessManagement.roles)}
            </Typography>
          </Stack>
        </Paper>

        <Tabs
          tabTitles={tabTitles}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        {renderTabContent()}
      </Box>
    </Box>
  );
};
